/**
 * SRES · Wireframe Editor Panel (Sprint P28 · Item 1)
 * ────────────────────────────────────────────────────
 * List, create-from-hull, boolean, smooth, repair, translate, delete.
 * Every operation writes a new version with parent_id → provides
 * deterministic undo (jump to parent) and redo (jump to latest child).
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Cube, Play, TrashSimple, ArrowClockwise, Waveform,
  Broom, MagnifyingGlassMinus, Path, ArrowUUpLeft, ArrowUUpRight,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";


function errMsg(e, fb = "Request failed") {
  const d = e?.response?.data;
  if (typeof d === "string") return d;
  if (typeof d?.detail === "string") return d.detail;
  if (Array.isArray(d?.detail)) return d.detail.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return e?.message || fb;
}


const HULL_SOURCES = [
  { key: "collars",             label: "Drillhole collars" },
  { key: "assays_above_cutoff", label: "Assays ≥ cutoff", needsCutoff: true },
  { key: "ore_blocks",          label: "Ore blocks (cutoff-passed)" },
];


function CreateHullForm({ pid, onCreated }) {
  const [name, setName]     = useState("");
  const [src,  setSrc]      = useState("collars");
  const [cutoff, setCutoff] = useState(0.5);

  const mut = useMutation({
    mutationFn: () => api.post(`/projects/${pid}/wireframe-editor/hull`, {
      name: name.trim() || `Hull ${new Date().toISOString().slice(0, 10)}`,
      source: src,
      ...(src === "assays_above_cutoff" ? { cutoff: Number(cutoff) } : {}),
    }).then((r) => r.data),
    onSuccess: () => { toast.success("Convex hull created"); setName(""); onCreated?.(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="border border-slate-200 bg-slate-50 rounded p-3 space-y-2"
         data-testid="wf-create-hull-form">
      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
        New convex hull
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)}
                className="h-8 text-[11px] col-span-2" data-testid="wf-hull-name" />
        <select value={src} onChange={(e) => setSrc(e.target.value)}
                 className="h-8 text-[11px] border border-slate-300 rounded px-1"
                 data-testid="wf-hull-source">
          {HULL_SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <Input type="number" step="0.05" value={cutoff}
                onChange={(e) => setCutoff(e.target.value)}
                disabled={src !== "assays_above_cutoff"}
                className="h-8 text-[11px]" data-testid="wf-hull-cutoff" />
      </div>
      <Button size="sm" onClick={() => mut.mutate()}
               disabled={mut.isPending}
               className="h-8 text-[11px] bg-brand-blue hover:bg-brand-blue/90 text-white"
               data-testid="wf-hull-submit">
        <Play size={12} className="mr-1" />
        {mut.isPending ? "Building…" : "Build hull"}
      </Button>
    </div>
  );
}


function WireframeRow({ pid, wf, byId, onChange, latestForParent }) {
  const qc = useQueryClient();
  const smooth = useMutation({
    mutationFn: () => api.post(`/projects/${pid}/wireframe-editor/${wf.id}/smooth`,
      { iterations: 3, lamb: 0.5 }).then((r) => r.data),
    onSuccess: () => { toast.success(`Smoothed · ${wf.name}`); onChange?.(); qc.invalidateQueries({ queryKey: ["wireframes", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const repair = useMutation({
    mutationFn: () => api.post(`/projects/${pid}/wireframe-editor/${wf.id}/repair`)
      .then((r) => r.data),
    onSuccess: () => { toast.success(`Repaired · ${wf.name}`); onChange?.(); qc.invalidateQueries({ queryKey: ["wireframes", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const del = useMutation({
    mutationFn: () => api.delete(`/projects/${pid}/wireframe-editor/${wf.id}`).then((r) => r.data),
    onSuccess: () => { toast.success(`Archived · ${wf.name}`); onChange?.(); qc.invalidateQueries({ queryKey: ["wireframes", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const parent = wf.parent_id ? byId[wf.parent_id] : null;
  const child  = latestForParent[wf.id];

  return (
    <div className="border border-slate-200 rounded bg-white p-3"
         data-testid={`wf-row-${wf.id}`}>
      <div className="flex items-center gap-2">
        <Cube size={13} className="text-brand-blue" weight="duotone" />
        <div className="text-[12px] font-semibold text-slate-800 truncate">{wf.name}</div>
        <Badge className="bg-slate-800 text-white text-[9px] font-mono">{wf.kind}</Badge>
        <Badge className={`text-[9px] font-mono ${
                wf.watertight ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"
              }`}>
          {wf.watertight ? "watertight" : "open"}
        </Badge>
        <div className="ml-auto text-[10px] font-mono text-slate-500">
          {wf.n_vertices?.toLocaleString()} V · {wf.n_faces?.toLocaleString()} F
          · vol {(wf.volume_m3 || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} m³
        </div>
      </div>
      <div className="text-[10px] font-mono text-slate-500 mt-1 flex items-center gap-3">
        <span>op · <b className="text-slate-700">{wf.op}</b></span>
        <span>sha · {(wf.sha256 || "").slice(0, 10)}…</span>
        <span>{wf.created_at ? new Date(wf.created_at).toLocaleString() : "—"}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1 pt-2 border-t border-slate-100 mt-2">
        <Button size="sm" variant="outline" className="h-7 text-[10px]"
                 onClick={() => smooth.mutate()} disabled={smooth.isPending}
                 data-testid={`wf-smooth-${wf.id}`}>
          <Waveform size={11} className="mr-1" /> Smooth
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[10px]"
                 onClick={() => repair.mutate()} disabled={repair.isPending}
                 data-testid={`wf-repair-${wf.id}`}>
          <Broom size={11} className="mr-1" /> Repair
        </Button>
        {parent && (
          <Button size="sm" variant="outline"
                   className="h-7 text-[10px] border-sky-300 text-sky-700"
                   onClick={() => onChange?.(parent.id)}
                   data-testid={`wf-undo-${wf.id}`}
                   title={`Undo → ${parent.name}`}>
            <ArrowUUpLeft size={11} className="mr-1" /> Undo
          </Button>
        )}
        {child && (
          <Button size="sm" variant="outline"
                   className="h-7 text-[10px] border-sky-300 text-sky-700"
                   onClick={() => onChange?.(child.id)}
                   data-testid={`wf-redo-${wf.id}`}
                   title={`Redo → ${child.name}`}>
            <ArrowUUpRight size={11} className="mr-1" /> Redo
          </Button>
        )}
        <div className="flex-1" />
        <Button size="sm" variant="outline"
                 className="h-7 text-[10px] text-rose-600 border-rose-200"
                 onClick={() => del.mutate()} disabled={del.isPending}
                 data-testid={`wf-archive-${wf.id}`}>
          <TrashSimple size={11} className="mr-1" /> Archive
        </Button>
      </div>
    </div>
  );
}


// ── Boolean form ────────────────────────────────────────────────────
function BooleanForm({ pid, items, onDone }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [op, setOp] = useState("intersection");
  const mut = useMutation({
    mutationFn: () => api.post(`/projects/${pid}/wireframe-editor/boolean`,
      { a_id: a, b_id: b, op }).then((r) => r.data),
    onSuccess: () => { toast.success(`${op} produced new wireframe`); onDone?.(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (items.length < 2) return null;
  return (
    <div className="border border-slate-200 bg-slate-50 rounded p-3 space-y-2"
         data-testid="wf-boolean-form">
      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">
        Boolean operation
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <select value={a} onChange={(e) => setA(e.target.value)}
                 className="h-8 text-[11px] border border-slate-300 rounded px-1"
                 data-testid="wf-bool-a">
          <option value="">Pick A…</option>
          {items.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <select value={op} onChange={(e) => setOp(e.target.value)}
                 className="h-8 text-[11px] border border-slate-300 rounded px-1"
                 data-testid="wf-bool-op">
          <option value="intersection">∩ Intersection</option>
          <option value="union">∪ Union</option>
          <option value="difference">− Difference (A − B)</option>
        </select>
        <select value={b} onChange={(e) => setB(e.target.value)}
                 className="h-8 text-[11px] border border-slate-300 rounded px-1"
                 data-testid="wf-bool-b">
          <option value="">Pick B…</option>
          {items.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <Button size="sm" onClick={() => mut.mutate()}
                 disabled={!a || !b || a === b || mut.isPending}
                 className="h-8 text-[11px] bg-brand-blue hover:bg-brand-blue/90 text-white"
                 data-testid="wf-bool-submit">
          {mut.isPending ? "Computing…" : "Run boolean"}
        </Button>
      </div>
    </div>
  );
}


export default function WireframeEditorPanel({ projectId }) {
  const q = useQuery({
    queryKey: ["wireframes", projectId],
    queryFn: () => api.get(`/projects/${projectId}/wireframe-editor`).then((r) => r.data),
    enabled: !!projectId,
    refetchInterval: 30_000,
  });

  const items = q.data?.items || [];
  const byId  = Object.fromEntries(items.map((w) => [w.id, w]));
  const latestForParent = {};
  for (const w of items) {
    if (w.parent_id) {
      const existing = latestForParent[w.parent_id];
      if (!existing || w.created_at > existing.created_at) {
        latestForParent[w.parent_id] = w;
      }
    }
  }

  return (
    <div className="border border-slate-200 rounded-lg bg-white shadow-sm"
         data-testid="wireframe-editor-panel">
      <div className="bg-slate-900 text-slate-100 px-4 py-3 border-b border-slate-800 flex items-center gap-2 rounded-t-lg">
        <Path size={16} className="text-sky-400" weight="duotone" />
        <h3 className="text-[14px] font-bold uppercase tracking-widest">
          Wireframe Editor
        </h3>
        <Badge className="bg-sky-500 text-white text-[9px] font-mono">
          HULL · BOOL · SMOOTH · REPAIR
        </Badge>
        <div className="ml-auto text-[10px] font-mono text-slate-400">
          {items.length} active wireframe{items.length === 1 ? "" : "s"}
          <button onClick={() => q.refetch()} title="Refresh"
                   className="ml-2 hover:text-white"
                   data-testid="wf-refresh">
            <ArrowClockwise size={12} />
          </button>
        </div>
      </div>
      <div className="p-4 space-y-3">
        <CreateHullForm pid={projectId} onCreated={() => q.refetch()} />
        <BooleanForm pid={projectId} items={items} onDone={() => q.refetch()} />
        {q.isLoading && (
          <div className="text-[11px] text-slate-500 py-4 text-center animate-pulse">
            Loading wireframes…
          </div>
        )}
        {!q.isLoading && items.length === 0 && (
          <div className="border border-dashed border-slate-300 rounded p-8 text-center">
            <MagnifyingGlassMinus size={36} className="mx-auto text-slate-300" weight="duotone" />
            <div className="text-[13px] font-bold text-slate-500 mt-2">NO WIREFRAMES</div>
            <div className="text-[10px] text-slate-400 mt-1">
              Build the first hull from real drillhole or block-model data.
            </div>
          </div>
        )}
        <div className="space-y-2 max-h-[420px] overflow-y-auto">
          {items.map((w) => (
            <WireframeRow key={w.id} pid={projectId} wf={w}
                            byId={byId} latestForParent={latestForParent}
                            onChange={() => q.refetch()} />
          ))}
        </div>
      </div>
    </div>
  );
}
